import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import { ClassType } from '../../../api';
import useSession, { refreshSession } from '../../Util/useSession';
import { useSessionLabelTasks, useSessionLineup } from './useLabelData';
import labelTasksService from '../../../services/labelTasks';
import { LabelTask } from '../../../services/sessionModels';
import PlayerLabelTask from './PlayerLabelTask';
import BallLabelTask from './BallLabelTask';
import { Coordinate } from '../../VideoPlayer/VideoTypes';
import { TextOverlay } from '../../VideoPlayer/VideoLayout';

export type Diff<T, U> = T extends U ? never : T;
export type TLabelTask = LabelTask;

export enum Cursor {
  Default = 'move',
  Waiting = 'progress',
  Add = 'copy',
  Verify = 'crosshair',
}

export type TaskStatus = {
  last_frame_id: number;
};

export type PositionBuffer = {
  page_start: number;
  page_size: number;
  value: Array<Coordinate>;
  video_coordinates?: Map<string, Array<Coordinate>>;
  ground_video_coordinates?: Map<string, Map<number, Coordinate>>;
  debug?: TextOverlay[];
};

export type FrameRange = {
  start_frame: number;
  end_frame?: number;
};

type TParams = {
  session: string;
  task: string;
};

interface Props extends RouteComponentProps<TParams> {}

const SessionLabelTask = (props: Props) => {
  const sessionId: string = props.match.params.session;
  const { session } = useSession(sessionId);
  const lineup = useSessionLineup(sessionId);
  const { labelTasks, refetch: refetchLabelTasks } = useSessionLabelTasks(sessionId);
  
  const [loadingLibraries, setLoadingLibraries] = useState<boolean>(document.readyState !== 'complete');

  const runId: string | null | undefined = session?.lastRun?.id;

  const taskId: string = props.match.params.task;
  const [task, setTask] = useState<TLabelTask | null>(null);

  const reloadTask = useCallback(async () => {
    const result = await labelTasksService.getLabelTask(taskId);
    setTask(result);
    refetchLabelTasks();
    refreshSession(sessionId);
  }, [taskId, sessionId, refetchLabelTasks]);

  useEffect(() => {
    let cancelled = false;
    labelTasksService.getLabelTask(taskId).then((result) => {
      if (!cancelled) setTask(result);
    });
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  const initApplication = () => {
    setLoadingLibraries(false);
  };
  document.onreadystatechange = () => {
    if (document.readyState === 'complete') {
      initApplication();
    }
  }

  return (
    <Container className="sciui-card sciui-m-l sciui-p-l sciui-bg-p-5">
      <Wrapper>
        {
          session && task && runId && !loadingLibraries 
          ? (
            task.classType === ClassType.Ball ? (
              <BallLabelTask session={session} task={task} runId={runId} reloadTask={reloadTask} />
            ) : (
              <PlayerLabelTask
                session={session}
                task={task}
                runId={runId}
                lineup={lineup}
                labelTasks={labelTasks ?? []}
                reloadTask={reloadTask}
              />
            )
          ) : loadingLibraries ? 'Loading libraries...' : "Loading data..."
        }
      </Wrapper>
    </Container>
  );
};

const Container = styled.div`
  min-height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px;
`;

export default SessionLabelTask;